// Query-string protocol shared with AirportWindStationSimulation. Reports must
// be complete and value-only so every accepted message can rebuild the view.
export const SCENARIOS = ['calm', 'steady', 'realign', 'highWind'];
export const PHASES = ['reset', 'calm', 'steady', 'aligning', 'realigning', 'generating', 'highWind'];
export const WIND_BANDS = ['calm', 'steady', 'high'];
export const ROTOR_BANDS = ['stopped', 'accelerating', 'running', 'decelerating'];
export const NOTICES = [
  'none',
  'unsafeGenerator',
  'unsafeInactive',
  'unsafeAlign',
  'missingRunwayAlert',
  'unexpectedRunwayAlert',
];

export const REPORT_FIELDS = [
  'v',
  'seq',
  'scenario',
  'phase',
  'windDirection',
  'windBand',
  'aligned',
  'nacelleAngle',
  'targetAngle',
  'rotorBand',
  'align',
  'generatorEnable',
  'active',
  'runwayWindAlert',
  'activeRequestId',
  'notice',
];

function readSingle(params, key) {
  const values = params.getAll(key);
  if (values.length !== 1 || values[0] === '') {
    throw new Error(`Missing or repeated report field: ${key}`);
  }
  return values[0];
}

function readInteger(params, key, minimum, maximum) {
  const token = readSingle(params, key);
  if (!/^\d+$/.test(token)) throw new Error(`Invalid integer field: ${key}`);
  const value = Number(token);
  if (!Number.isSafeInteger(value) || value < minimum || value > maximum) {
    throw new Error(`Out-of-range report field: ${key}`);
  }
  return value;
}

function readBoolean(params, key) {
  const token = readSingle(params, key);
  if (token !== '0' && token !== '1') throw new Error(`Invalid boolean field: ${key}`);
  return token === '1';
}

function readEnum(params, key, allowed) {
  const value = readSingle(params, key);
  if (!allowed.includes(value)) throw new Error(`Invalid ${key}: ${value}`);
  return value;
}

export function parseReport(raw) {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 1024) {
    throw new Error('Report must be a bounded query string');
  }

  const params = new URLSearchParams(raw);
  for (const key of REPORT_FIELDS) readSingle(params, key);
  if (readSingle(params, 'v') !== '1') throw new Error('Unsupported report version');

  return {
    v: 1,
    seq: readInteger(params, 'seq', 1, 0xffffffff),
    scenario: readEnum(params, 'scenario', SCENARIOS),
    phase: readEnum(params, 'phase', PHASES),
    windDirection: readInteger(params, 'windDirection', 0, 359),
    windBand: readEnum(params, 'windBand', WIND_BANDS),
    aligned: readBoolean(params, 'aligned'),
    nacelleAngle: readInteger(params, 'nacelleAngle', 0, 359),
    targetAngle: readInteger(params, 'targetAngle', 0, 359),
    rotorBand: readEnum(params, 'rotorBand', ROTOR_BANDS),
    align: readBoolean(params, 'align'),
    generatorEnable: readBoolean(params, 'generatorEnable'),
    active: readBoolean(params, 'active'),
    runwayWindAlert: readBoolean(params, 'runwayWindAlert'),
    activeRequestId: readInteger(params, 'activeRequestId', 0, 0xffffffff),
    notice: readEnum(params, 'notice', NOTICES),
  };
}

export function serializeCommand(requestId, action, name) {
  if (!Number.isInteger(requestId) || requestId <= 0 || requestId > 0xffffffff) {
    throw new Error('requestId must be a non-zero uint32');
  }
  if (!['sync', 'scenario', 'restart'].includes(action)) throw new Error('Invalid action');
  if (action === 'scenario' && !SCENARIOS.includes(name)) throw new Error('Invalid scenario');
  if (action !== 'scenario' && name !== undefined) throw new Error('Only scenario actions have a name');

  const params = new URLSearchParams({ v: '1', requestId: String(requestId), action });
  if (name !== undefined) params.set('name', name);
  return params.toString();
}
